import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, UserPlus, UserMinus, Check, X, ChevronRight } from "lucide-react";
import { api, useAuth } from "../contexts/AuthContext";
import AvatarDisplay from "../components/AvatarDisplay";
import BottomNav from "../components/BottomNav";

export default function FriendsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ friends: [], pending_requests: [] });
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState("");

  useEffect(() => { fetchFriends(); }, []);

  const fetchFriends = async () => {
    try {
      const { data: d } = await api.get("/friends");
      setData(d);
    } catch {}
    setLoading(false);
  };

  const notify = (msg) => { setNotification(msg); setTimeout(() => setNotification(""), 3000); };

  const handleAccept = async (fromUserId) => {
    try { await api.post(`/friends/accept/${fromUserId}`); fetchFriends(); notify("Friend request accepted!"); }
    catch { notify("Failed to accept"); }
  };

  const handleRemove = async (friendUserId) => {
    try { await api.post(`/friends/remove/${friendUserId}`); fetchFriends(); notify("Friend removed"); }
    catch { notify("Failed to remove"); }
  };

  return (
    <div className="min-h-screen bg-[#07070B]">
      {/* Header */}
      <header className="h-14 bg-[#0E0E17]/90 backdrop-blur-xl border-b border-purple-500/20 flex items-center px-4 gap-3 sticky top-0 z-40">
        <button data-testid="back-from-friends-btn" onClick={() => navigate("/lobby")} className="hidden md:flex text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-white font-[Outfit]">Friends</h1>
      </header>

      <div className="max-w-2xl mx-auto p-4 pb-24 md:pb-4 space-y-6">
        {notification && (
          <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl text-purple-300 text-sm text-center">
            {notification}
          </div>
        )}

        {/* Pending requests */}
        {data.pending_requests.length > 0 && (
          <section>
            <h2 className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-3">
              Friend Requests ({data.pending_requests.length})
            </h2>
            <div className="space-y-2">
              {data.pending_requests.map(u => (
                <motion.div
                  key={u.user_id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-3 bg-[#0E0E17] border border-amber-500/20 rounded-xl p-4"
                >
                  <AvatarDisplay avatarId={u.avatar_id} size="md" status={u.status} showStatus />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white">{u.alias}</p>
                    <p className="text-xs text-slate-400">Wants to be friends</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`accept-friend-${u.user_id}`}
                      onClick={() => handleAccept(u.user_id)}
                      className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`decline-friend-${u.user_id}`}
                      onClick={() => handleRemove(u.user_id)}
                      className="w-9 h-9 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Friends list */}
        <section>
          <h2 className="text-xs font-mono uppercase tracking-widest text-purple-400 mb-3">
            Friends ({data.friends.length})
          </h2>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : data.friends.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="w-12 h-12 mx-auto mb-3 text-slate-600" />
              <p className="text-slate-400 font-semibold">No friends yet</p>
              <p className="text-slate-600 text-sm mt-2 max-w-xs mx-auto leading-relaxed">
                Hover over anyone in the <span className="text-purple-400">Who's On</span> sidebar on the lobby,
                or click their avatar inside a chatroom to send a friend request.
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="friends-list">
              {data.friends.map(f => (
                <motion.div
                  key={f.user_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-3 bg-[#0E0E17] border border-purple-500/15 rounded-xl p-4 hover:border-purple-500/30 transition-colors"
                >
                  <AvatarDisplay avatarId={f.avatar_id} size="md" status={f.is_online ? "online" : "offline"} showStatus />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white">{f.alias}</p>
                    {f.is_online ? (
                      f.current_room_name ? (
                        <button
                          data-testid={`friend-join-room-${f.user_id}`}
                          onClick={() => navigate(`/room/${f.current_room_id}`)}
                          className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors mt-0.5"
                        >
                          <span className="truncate">In {f.current_room_name}</span>
                          <ChevronRight className="w-3 h-3 shrink-0" />
                          <span className="shrink-0">Join them</span>
                        </button>
                      ) : (
                        <p className="text-xs text-emerald-400 mt-0.5">Online · in the lobby</p>
                      )
                    ) : (
                      <p className="text-xs text-slate-500 mt-0.5">Offline</p>
                    )}
                  </div>
                  <button
                    data-testid={`remove-friend-${f.user_id}`}
                    onClick={() => handleRemove(f.user_id)}
                    className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-500 hover:text-red-400 hover:border-red-500/40 transition-colors shrink-0"
                  >
                    <UserMinus className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </div>
      <BottomNav active="friends" />
    </div>
  );
}
